import { useState, useEffect } from 'react';
import { Modal, Button, Badge } from '../../../shared/ui';
import { useQuickAnalysis } from '../../../shared/api';
import { useQueryClient } from '@tanstack/react-query';
import { sileo } from 'sileo';
import { Code, Sparkles, Zap, CheckCircle, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useAnalysisStore } from '../../../shared/lib/store/analysisStore';
import { QuickAnalysisResult } from '../../../entities/analysis';

interface DirectCodeAnalysisModalProps {
    isOpen: boolean;
    onClose: () => void;
    projectId?: string;
    initialCode?: string;
    fileName?: string;
}

export function DirectCodeAnalysisModal({
    isOpen,
    onClose,
    projectId,
    initialCode = '',
    fileName = 'snippet.ts'
}: DirectCodeAnalysisModalProps) {
    const [code, setCode] = useState(initialCode);
    const [language, setLanguage] = useState<'javascript' | 'typescript' | 'python' | 'csharp'>('typescript');
    const [fileNameState, setFileNameState] = useState(fileName);
    const qc = useQueryClient();
    const addLocalResult = useAnalysisStore(state => state.addResult);
    const { mutate: analyze, isPending, data: result, reset } = useQuickAnalysis();

    // Language detection logic
    const detectLanguage = (content: string, name?: string): 'javascript' | 'typescript' | 'python' | 'csharp' => {
        const ext = name?.split('.').pop()?.toLowerCase();
        if (ext === 'py') return 'python';
        if (ext === 'js' || ext === 'jsx' || ext === 'mjs') return 'javascript';
        if (ext === 'ts' || ext === 'tsx') return 'typescript';
        if (ext === 'cs') return 'csharp';

        // C# indicators
        if (
            content.includes('using System') ||
            content.includes('namespace ') ||
            content.includes('public class ') ||
            content.includes('Console.WriteLine(')
        ) {
            return 'csharp';
        }

        // Python indicators
        if (
            content.includes('def ') ||
            content.includes('import os') ||
            content.includes('import sys') ||
            content.includes('print(') ||
            content.includes('elif ') ||
            /^\s*class\s+\w+:/m.test(content)
        ) {
            return 'python';
        }

        // TS indicators: interfaces, types, type annotations
        if (content.includes('interface ') || content.includes('type ') || /:\s*(string|number|boolean|any|void|\[)/.test(content)) {
            return 'typescript';
        }

        return 'javascript';
    };

    // Sync initial code when modal opens or initialCode changes
    useEffect(() => {
        if (isOpen) {
            if (initialCode) {
                setCode(initialCode);
                const detected = detectLanguage(initialCode, fileName);
                setLanguage(detected);
                setFileNameState(fileName === 'snippet.ts' ? `snippet.${detected === 'python' ? 'py' : detected === 'javascript' ? 'js' : detected === 'csharp' ? 'cs' : 'ts'}` : fileName);
            } else {
                setLanguage('typescript');
                setFileNameState('snippet.ts');
            }
            reset();
        }
    }, [isOpen, initialCode, fileName, reset]);

    const handleLanguageChange = (lang: 'javascript' | 'typescript' | 'python' | 'csharp') => {
        setLanguage(lang);
        const ext = lang === 'python' ? 'py' : lang === 'javascript' ? 'js' : lang === 'csharp' ? 'cs' : 'ts';
        setFileNameState(`snippet.${ext}`);
    };

    const handleAnalyze = (isAI: boolean = true) => {
        if (!code.trim()) {
            sileo.error({ title: 'Input Error', description: 'Please paste some code to analyze.' });
            return;
        }

        analyze({
            project_id: projectId,
            snippets: [{
                content: code,
                language: language,
                file_path: fileNameState
            }],
            analysis_types: isAI 
                ? ['security', 'performance', 'code_quality', 'ai_logic', 'context_aware']
                : ['security', 'performance', 'code_quality']
        }, {
            onSuccess: (data: QuickAnalysisResult) => {
                sileo.success({ title: 'Analysis Complete', description: 'Quick analysis finished successfully.' });

                // Persist locally
                if (projectId) {
                    addLocalResult(projectId, fileNameState, data);
                    qc.invalidateQueries({ queryKey: ['analysis', 'project-summary', projectId] });
                    qc.invalidateQueries({ queryKey: ['reports', 'project-reports', projectId] });
                }
            },
            onError: (err: any) => {
                sileo.error({ title: 'Analysis Failed', description: err.message || 'Error processing snippet' });
            }
        });
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Direct Code Analysis"
            size="xl"
            footer={
                <div className="flex justify-between w-full">
                    <Button variant="ghost" onClick={onClose} corners="asymmetric">Close</Button>
                    <div className="flex gap-3">
                        <Button
                            variant="primary"
                            onClick={() => handleAnalyze(false)}
                            disabled={isPending || !code.trim()}
                            corners="asymmetric-inverse"
                            className="bg-white text-black border border-border hover:bg-neutral-50 shadow-sm font-bold"
                        >
                            {isPending ? <Loader2 className="animate-spin h-4 w-4 mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
                            Quick Linter Scan
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => handleAnalyze(true)}
                            disabled={isPending || !code.trim()}
                            corners="asymmetric-inverse"
                            className="relative overflow-hidden bg-gradient-to-br from-white via-white to-cyan-500/30 text-black border border-border shadow-sm hover:shadow-md transition-all font-bold group"
                        >
                            <span className="relative z-10 flex items-center">
                                {isPending ? <Loader2 className="animate-spin h-4 w-4 mr-2" /> : <Sparkles className="h-4 w-4 mr-2 text-cyan-600" />}
                                Run AI Pro Analysis
                            </span>
                            <div className="absolute bottom-0 right-0 w-8 h-8 bg-cyan-500/20 blur-xl group-hover:bg-cyan-500/40 transition-colors" />
                        </Button>
                    </div>
                </div>
            }
        >
            <div className="space-y-6">
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-text-primary font-mono flex items-center gap-2">
                            <Code className="h-4 w-4" />
                            Paste Code Snippet
                        </label>
                        <div className="flex gap-2">
                            {(['javascript', 'typescript', 'python', 'csharp'] as const).map(lang => (
                                <button
                                    key={lang}
                                    onClick={() => handleLanguageChange(lang)}
                                    className={`px-2 py-0.5 text-[10px] font-mono rounded border transition-colors ${language === lang ? 'bg-primary/20 border-primary text-primary' : 'bg-surface border-border text-text-muted hover:border-border-hover'}`}
                                >
                                    {lang === 'javascript' ? 'JS' : lang === 'typescript' ? 'TS' : lang === 'python' ? 'PYTHON' : 'C#'}
                                </button>
                            ))}
                        </div>
                    </div>
                    <textarea
                        value={code}
                        onChange={(e) => {
                            const newCode = e.target.value;
                            setCode(newCode);
                            const detected = detectLanguage(newCode, fileNameState);
                            setLanguage(detected);
                            setFileNameState(fileName === 'snippet.ts' ? `snippet.${detected === 'python' ? 'py' : detected === 'javascript' ? 'js' : detected === 'csharp' ? 'cs' : 'ts'}` : fileName);
                        }}
                        placeholder="// Paste your code here..."
                        className="w-full h-64 p-4 bg-surface border border-border rounded-asymmetric-md font-mono text-sm focus:ring-2 focus:ring-primary focus:outline-none resize-none"
                    />
                    <div className="text-[10px] text-text-muted font-mono flex items-center gap-1 mt-1">
                        Analyzing as: <span className="text-primary">{fileNameState}</span>
                    </div>
                </div>

                {result && (
                    <div className="space-y-4 pt-4 border-t border-border">
                        <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-text-primary font-mono">Analysis Results</h3>
                            <div className="flex gap-2">
                                <Badge severity="info">{result.metrics.complexity} Complexity</Badge>
                                <Badge severity="info">{result.metrics.lines} Lines</Badge>
                            </div>
                        </div>

                        {result.ai_insights && (
                            <div className="space-y-3 mb-6">
                                <div className="font-bold text-primary font-mono">DevLint AI Insights</div>
                                {result.ai_insights
                                    .split(/(?=Tipo de problema:|\*\*Tipo de problema:\*\*)/g)
                                    .filter(b => b.trim())
                                    .map((insightBox, idx) => (
                                        <div key={idx} className="p-4 bg-primary/10 border border-primary/30 rounded-asymmetric-md text-sm font-sans text-text-primary">
                                            <div className="prose prose-sm prose-invert max-w-none
                                            prose-p:leading-relaxed prose-p:mb-2
                                            prose-strong:text-primary prose-strong:font-bold
                                            prose-ul:list-disc prose-ul:ml-4 prose-li:mb-1
                                            prose-li:marker:text-primary/70
                                            prose-code:text-warning prose-code:bg-warning/10 prose-code:px-1 p-0.5 prose-code:rounded
                                        ">
                                                <ReactMarkdown>
                                                    {insightBox}
                                                </ReactMarkdown>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        )}

                        {result.issues.length === 0 && !result.ai_insights ? (
                            <div className="flex items-center gap-2 p-4 bg-success/10 border border-success/30 rounded-asymmetric-md text-success text-sm font-mono">
                                <CheckCircle className="h-4 w-4" />
                                No issues found in this snippet!
                            </div>
                        ) : result.issues.length > 0 && (
                            <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                                {result.issues.map((issue, idx) => {
                                    const codeLines = code.split('\n');
                                    const issueLineIndex = Math.max(0, issue.line_start - 1);
                                    const issueCodeSnippet = codeLines[issueLineIndex] !== undefined ? codeLines[issueLineIndex] : '';

                                    return (
                                        <div key={idx} className="p-4 bg-surface-hover border border-border rounded-asymmetric-sm space-y-2">
                                            <div className="flex items-center justify-between">
                                                <Badge severity={issue.severity}>{issue.severity.toUpperCase()}</Badge>
                                                <span className="text-xs text-text-muted font-mono">L{issue.line_start}-L{issue.line_end}</span>
                                            </div>
                                            <p className="text-sm font-medium text-text-primary">{issue.title}</p>
                                            <p className="text-xs text-text-muted line-clamp-2">{issue.description}</p>

                                            {issueCodeSnippet && (
                                                <div className="mt-3 bg-background p-2 rounded border border-border/50 font-mono text-xs overflow-x-auto">
                                                    <div className="flex text-text-muted">
                                                        <span className="w-8 flex-shrink-0 text-right pr-2 select-none border-r border-border/50 mr-3">{issue.line_start}</span>
                                                        <span className="text-error mr-2 font-bold">➔</span>
                                                        <span className="whitespace-pre">{issueCodeSnippet}</span>
                                                    </div>
                                                </div>
                                            )}

                                            {issue.suggestion && (
                                                <div className="mt-2 p-2 bg-primary/5 border border-primary/20 rounded text-xs font-mono text-primary">
                                                    <strong>Suggestion:</strong> {issue.suggestion}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
}
